// P05-4-3: Coach Feed Page
// Displays Office Hours threads from a specific verified coach

import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle, Loader2, MessageSquare } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { supabase } from '@/integrations/supabase/client';
// import { useFeedPosts } from '@/features/clubhouse/api/useFeedPosts';

interface CoachProfile {
  id: string;
  user_id: string;
  full_name: string | null;
  coach_bio: string | null;
  specialties: string[] | null;
  certifications: string[] | null;
  verified: boolean;
}

async function fetchCoachProfile(userId: string): Promise<CoachProfile | null> {
  const { data, error } = await supabase
    .from('profiles')
    .select('id, user_id, full_name, coach_bio, specialties, certifications, verified')
    .eq('user_id', userId)
    .eq('is_coach', true)
    .eq('verified', true)
    .maybeSingle();

  if (error) {
    throw new Error(`Failed to fetch coach: ${error.message}`);
  }

  return data;
}

export default function CoachFeedPage() {
  const { coachId } = useParams<{ coachId: string }>();

  const { data: coach, isLoading: coachLoading, error: coachError } = useQuery<CoachProfile | null>({
    queryKey: ['coach-profile', coachId],
    queryFn: () => fetchCoachProfile(coachId!),
    enabled: !!coachId,
  });

  if (!coachId) {
    return (
      <div className="text-center py-12">
        <p className="text-destructive font-semibold">Coach ID required</p>
      </div>
    );
  }

  if (coachLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="animate-spin" size={32} />
      </div>
    );
  }

  if (coachError || !coach) {
    return (
      <div className="text-center py-12">
        <p className="text-destructive font-semibold">Coach not found</p>
        <p className="text-sm text-muted-foreground mt-2">
          This coach may not be verified or doesn't exist.
        </p>
      </div>
    );
  }

  const initials = coach.full_name
    ?.split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) || '??';

  return (
    <div className="space-y-6">
      {/* Coach Header Banner */}
      <Card className="bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200">
        <CardHeader>
          <div className="flex items-start gap-4">
            <Avatar className="w-16 h-16">
              <AvatarFallback className="text-lg">{initials}</AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <CardTitle className="flex items-center gap-2">
                {coach.full_name || 'Coach'}
                <CheckCircle className="w-5 h-5 text-emerald-600" />
                <Badge variant="secondary" className="ml-2">
                  Verified Coach
                </Badge>
              </CardTitle>
              {coach.coach_bio && (
                <CardDescription className="mt-2 text-base">
                  {coach.coach_bio}
                </CardDescription>
              )}
              {coach.specialties && coach.specialties.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {coach.specialties.map((spec) => (
                    <Badge key={spec} variant="outline">
                      {spec}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Office Hours Header */}
      <div className="flex items-center gap-2">
        <MessageSquare className="w-5 h-5 text-emerald-600" />
        <h2 className="text-2xl font-bold">Office Hours</h2>
      </div>
      <p className="text-muted-foreground">
        Q&A threads and tips from {coach.full_name || 'this coach'}
      </p>

      {/* Feed with Coach Context Filter */}
      <div className="mt-6">
        <CoachFeedContent coachId={coachId} />
      </div>
    </div>
  );
}

// Separate component that uses FeedPage but filters by coach context
function CoachFeedContent({ coachId }: { coachId: string }) {
  // Use FeedPage with coach context filter
  // FeedPage will need to be updated to accept context props
  // For now, we'll create a simplified version
  return (
    <div className="space-y-4">
      <CoachFeedPosts coachId={coachId} />
    </div>
  );
}

// Custom feed component for coach context
// Archived - useFeedPosts removed
function CoachFeedPosts({ coachId }: { coachId: string }) {

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="animate-spin" size={32} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-destructive font-semibold">Failed to load posts</p>
        <p className="text-sm text-muted-foreground mt-2">{error.message}</p>
      </div>
    );
  }

  if (posts.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-lg font-semibold">No Office Hours posts yet</p>
        <p className="text-muted-foreground mt-2">
          This coach hasn't posted any Office Hours threads yet.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {posts.map((post) => (
        <Card key={post.id}>
          <CardHeader>
            <CardTitle className="text-sm">{post.author.name}</CardTitle>
            <CardDescription>{new Date(post.created_at).toLocaleDateString()}</CardDescription>
          </CardHeader>
          <CardContent>
            <p>{post.body}</p>
          </CardContent>
        </Card>
      ))}
      {hasNextPage && (
        <div className="flex justify-center pt-4">
          <button
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
            className="text-emerald-600 hover:underline"
          >
            {isFetchingNextPage ? 'Loading...' : 'Load More'}
          </button>
        </div>
      )}
    </div>
  );
}

